
<template>
  <div class="text-center">
    <div class="pa-3 text-center" v-if="iframeLoading">
      Rendering template...
      <div style="width: 300px; margin: auto" v-if="iframeLoading">
        <v-progress-linear
          indeterminate
          color="primary"
          :size="40"
        ></v-progress-linear>
      </div>
    </div>
    <i-frame
      ref="iframe"
      class="iframe elevation-2"
      :class="resolution"
      v-show="!iframeLoading"
    >
      <a-layout-without-head
        :siteTheme="project.siteTheme"
        v-if="project.siteTheme"
        :project="project"
      >
        <a-block
          v-for="(block, index) in blocks"
          :key="block.id"
          :block="block"
          :nextBlock="blocks[index + 1]"
          :highlight="editingBlockId === block.id"
          :showBlockManagement="showBlockManagement"
          @add-new-block="$emit('add-new-block', { block, index })"
          @edit-click="$emit('edit-click', block)"
          @up-click="$emit('up-click', block)"
          @down-click="$emit('down-click', block)"
          @delete-click="$emit('delete-click', block)"
        ></a-block>
      </a-layout-without-head>
    </i-frame>
  </div>
</template>

<script>
import { scriptsGenerator, stylesGenerator } from "landly-uiblocks";

export default {
  props: [
    "project",
    "blocks",
    "editingBlockId",
    "resolution",
    "showBlockManagement",
  ],
  data() {
    return {
      iframeLoading: false,
    };
  },
  watch: {
    "project.siteTheme.fontFamily1": function (newVal) {
      if (newVal) {
        this.updateGoogleFontFaceLink(newVal);
      }
    },
    "project.siteTheme.fontFamily2": function (newVal) {
      if (newVal) {
        this.updateGoogleFontFaceLink(newVal);
      }
    },
  },
  async created() {
    if (!this.project || !this.project.siteTheme) {
      return;
    }
    await this.iframeInjectResources();
  },
  methods: {
    injectStylesheetLink(href) {
      const iframeRef = this.$refs.iframe;
      return new Promise((resolve) => {
        const iframe = iframeRef.$el;
        var head =
          iframe.contentWindow.document.getElementsByTagName("head")[0];
        var link = iframe.contentWindow.document.createElement("link");
        link.href = href;
        link.rel = "stylesheet";
        head.appendChild(link);
        resolve();
      });
    },
    injectStylesheetCss(css) {
      const iframeRef = this.$refs.iframe;
      return new Promise((resolve) => {
        const iframe = iframeRef.$el;
        var head =
          iframe.contentWindow.document.getElementsByTagName("head")[0];
        var link = iframe.contentWindow.document.createElement("style");
        link.textContent = css;
        head.appendChild(link);
        resolve();
      });
    },
    injectScriptContent(script) {
      const iframeRef = this.$refs.iframe;
      const iframe = iframeRef.$el;
      var head = iframe.contentWindow.document.getElementsByTagName("head")[0];
      var s = iframe.contentWindow.document.createElement("script");
      s.type = "text/javascript";
      s.innerHTML = script;
      head.appendChild(s);
    },
    async injectScriptSrc(url) {
      return new Promise((resolve) => {
        const iframeRef = this.$refs.iframe;
        const iframe = iframeRef.$el;
        var head =
          iframe.contentWindow.document.getElementsByTagName("head")[0];
        var s = iframe.contentWindow.document.createElement("script");
        s.async = true;
        s.src = url;
        head.appendChild(s);
        s.addEventListener("load", () => {
          resolve();
        });
      });
    },
    async updateGoogleFontFaceLink(fontFamily) {
      await this.injectStylesheetLink(
        "https://fonts.googleapis.com/css2?family=" +
          fontFamily +
          "&display=swap"
      );
    },
    async iframeInjectResources() {
      this.iframeLoading = true;

      const iframeRef = this.$refs.iframe;
      if (!iframeRef) {
        setTimeout(() => {
          this.iframeInjectResources();
        }, 10);
        return;
      }

      setTimeout(async () => {
        const fa =
          "https://stackpath.bootstrapcdn.com/font-awesome/4.7.0/css/font-awesome.min.css";
        const slickCss =
          "https://cdn.landly.page/assets/vue-slick-carousel.css";
        const slickThemeCss =
          "https://cdn.landly.page/assets/vue-slick-carousel-theme.css";
        const tw = "https://cdn.landly.page/assets/tailwind.min.css";
        await this.injectStylesheetLink(fa);
        await this.injectStylesheetLink(slickCss);
        await this.injectStylesheetLink(slickThemeCss);
        await this.injectStylesheetLink(tw);
        await this.injectScriptSrc(
          "https://cdn.landly.page/assets/jquery-3.4.1.min.js"
        );
        await this.injectScriptSrc(
          "https://cdn.landly.page/assets/jquery.validate.min.js"
        );
        await this.injectScriptSrc("https://js.stripe.com/v3/");
        await this.updateGoogleFontFaceLink(this.project.siteTheme.fontFamily1);
        await this.updateGoogleFontFaceLink(this.project.siteTheme.fontFamily2);
        await this.injectStylesheetCss(stylesGenerator(this.project.id));
        this.injectScriptContent(
          scriptsGenerator(this.project.id, {
            isInDevelopment: true,
          })
        );
        this.iframeLoading = false;
      }, 500);
    },
  },
};
</script>

<style scoped>
.preview-container {
  width: 100%;
  height: 100%;
  border: none;
  margin-top: 120px;
  transition: margin-left 200ms;
}
.preview-container.normal {
  margin-left: 410px;
}

.preview-container.large {
  margin-left: 0px;
}

.iframe {
  height: 100%;
  border: none;
  overflow-y: auto;
  transition: width 500ms;
}

.iframe-container {
  height: 100%;
}

.iframe-container.mobile,
.iframe-container.tablet {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.iframe.desktop {
  width: 100%;
  height: calc(100vh - 120px);
}

.iframe.tablet {
  width: 768px;
  height: 700px;
}

.iframe.mobile {
  width: 375px;
  height: 600px;
}
</style>